import React, { useState } from 'react';
import { X, UserPlus, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { authService } from '../../../services/authService';
import { supabase } from '../../../lib/supabase';

const AddUserModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    programStartDate: new Date()?.toISOString()?.split('T')?.[0]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
    setError('');
  };

  const validateForm = () => {
    if (!formData?.email || !formData?.password || !formData?.fullName || !formData?.programStartDate) {
      setError('All fields are required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Create user account with Supabase Auth
      const { user, error: signUpError } = await authService?.signUp(
        formData?.email,
        formData?.password,
        {
          fullName: formData?.fullName,
          role: 'client'
        }
      );

      if (signUpError) {
        setError(signUpError?.message || 'Failed to create user account');
        setLoading(false);
        return;
      }

      // Update user profile with program start date
      if (user?.id) {
        const { error: profileError } = await supabase?.from('user_profiles')?.update({
            program_start_date: formData?.programStartDate,
            full_name: formData?.fullName,
            role: 'client',
            status: 'active'
          })?.eq('id', user?.id);

        if (profileError) {
          console.error('Error updating user profile:', profileError);
          setError('User created but failed to set program start date');
        }
      }

      setSuccess(true);
      
      // Show success message for 1.5 seconds then close
      setTimeout(() => {
        setSuccess(false);
        resetForm();
        onClose();
        onSuccess?.();
      }, 1500);

    } catch (err) {
      console.error('Error creating user:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      programStartDate: new Date()?.toISOString()?.split('T')?.[0]
    });
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <UserPlus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add New Client</h2>
              <p className="text-sm text-gray-500">Create a new client account</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">Client account created successfully!</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Full Name */}
          <div>
            <Input
              label="Full Name"
              name="fullName"
              type="text"
              value={formData?.fullName}
              onChange={handleChange}
              placeholder="Enter client's full name"
              required
              disabled={loading || success}
            />
          </div>

          {/* Email */}
          <div>
            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData?.email}
              onChange={handleChange}
              placeholder="client@example.com"
              required
              disabled={loading || success}
            />
          </div>

          {/* Password */}
          <div>
            <Input
              label="Password"
              name="password"
              type="password"
              value={formData?.password}
              onChange={handleChange}
              placeholder="Minimum 6 characters"
              required
              disabled={loading || success}
            />
            <p className="mt-1 text-xs text-gray-500">
              Must be at least 6 characters long
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <Input
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData?.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
              required
              disabled={loading || success}
            />
          </div>

          {/* Program Start Date */}
          <div>
            <Input
              label="Program Start Date"
              name="programStartDate"
              type="date"
              value={formData?.programStartDate}
              onChange={handleChange}
              required
              disabled={loading || success}
            />
            <p className="mt-1 text-xs text-gray-500">
              This determines the client's curriculum schedule
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading || success}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || success}
              loading={loading}
              fullWidth
            >
              {loading ? 'Creating Account...' : 'Create Client'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;